import {NgModule} from '@angular/core';
import {SplitterModule} from 'primeng/splitter';
import {CN_ENTITY_PROVIDER} from './cn.entity-provider';
import {CoreModule} from 'features/core/core.module';
import {CnConfigPage} from './page/cn-config-page/cn-config.page';
import {CnModelComparisonPage} from './page/cn-model-comparison-page/cn-model-comparison.page';
import {CnDashboardPage} from './page/cn-dashboard-page/cn-dashboard.page';
import {CnReconciliationPage} from './page/cn-reconciliation-page/cn-reconciliation.page';
import {CnImportPage} from './page/cn-import-page/cn-import.page';
import {CnImportReviewPage} from './page/cn-import-review-page/cn-import-review.page';
import {CnCommissionModelObjectPage} from './page/cn-commission-model-object-page/cn-commission-model-object.page';
import {CnCommissionRuleObjectPage} from './page/cn-commission-rule-object-page/cn-commission-rule-object.page';
import {CnCommissionStatementObjectPage} from './page/cn-commission-statement-object-page/cn-commission-statement-object.page';
import {CnContractCommissionPage} from './page/cn-contract-commission-page/cn-contract-commission.page';

// Object components — CommissionRule
import {
    CnCommissionRuleObjectComponent
} from './component/protected-components/object-base/cn-commission-rule-object/cn-commission-rule-object.component';
import {
    CnCommissionRuleObjectBaseViewComponent
} from './component/protected-components/object-base/cn-commission-rule-object/views/cn-commission-rule-object-base-view/cn-commission-rule-object-base-view.component';
import {
    CnCommissionRuleFullComponent
} from './component/protected-components/object-base/cn-commission-rule-object/views/cn-commission-rule-full/cn-commission-rule-full.component';
import {
    CnCommissionRuleSideComponent
} from './component/protected-components/object-base/cn-commission-rule-object/views/cn-commission-rule-side/cn-commission-rule-side.component';

// Object components — CommissionModel
import {
    CnCommissionModelObjectComponent
} from './component/protected-components/object-base/cn-commission-model-object/cn-commission-model-object.component';
import {
    CnCommissionModelObjectBaseViewComponent
} from './component/protected-components/object-base/cn-commission-model-object/views/cn-commission-model-object-base-view/cn-commission-model-object-base-view.component';
import {
    CnCommissionModelFullComponent
} from './component/protected-components/object-base/cn-commission-model-object/views/cn-commission-model-full/cn-commission-model-full.component';
import {
    CnCommissionModelSideComponent
} from './component/protected-components/object-base/cn-commission-model-object/views/cn-commission-model-side/cn-commission-model-side.component';

// Object component — CommissionStatement
import {
    CnCommissionStatementObjectComponent
} from './component/object-components/cn-commission-statement-object/cn-commission-statement-object.component';

// Protected components
import {
    CnModelComparisonComponent
} from './component/protected-components/cn-model-comparison/cn-model-comparison.component';
import {
    CnModelComparisonResultComponent
} from './component/protected-components/cn-model-comparison-result/cn-model-comparison-result.component';
import {
    CnDecisionSnapshotComponent
} from './component/protected-components/cn-decision-snapshot/cn-decision-snapshot.component';
import {
    CnExpectationTraceComponent
} from './component/protected-components/cn-expectation-trace/cn-expectation-trace.component';
import {
    CnDataQualityPanelComponent
} from './component/protected-components/cn-data-quality-panel/cn-data-quality-panel.component';
import {
    CnRulePreviewComponent
} from './component/protected-components/cn-rule-preview/cn-rule-preview.component';
import {
    CnActivityInboxComponent
} from './component/protected-components/cn-activity-inbox/cn-activity-inbox.component';
import {
    CnReconciliationPanelComponent
} from './component/protected-components/cn-reconciliation-panel/cn-reconciliation-panel.component';
import {
    CnAbgleichComparisonComponent
} from './component/protected-components/cn-abgleich-comparison/cn-abgleich-comparison.component';
import {
    CnImportStartComponent
} from './component/protected-components/cn-import-start/cn-import-start.component';
import {
    CnImportReviewComponent
} from './component/protected-components/cn-import-review/cn-import-review.component';
import {
    CnContractCommissionComponent
} from './component/protected-components/cn-contract-commission/cn-contract-commission.component';

/**
* Feature module for cn (Commission iNcome).
*
* Declares and exports the cn page, object-component and protected-component classes.
* Object components are also registered in CN_ENTITY_PROVIDER (objectComponent[]) so the
* framework resolves them for the click-to-object-page chain (CONFIG-015).
*/
@NgModule({
    declarations: [
        // Pages
        CnConfigPage,
        CnModelComparisonPage,
        CnDashboardPage,
        CnImportPage,
        CnImportReviewPage,
        CnReconciliationPage,
        CnCommissionModelObjectPage,
        CnCommissionRuleObjectPage,
        CnCommissionStatementObjectPage,
        CnContractCommissionPage,

        // CommissionStatement object component (Übersicht / Buchungen / Zeilen / Dokument)
        CnCommissionStatementObjectComponent,

        // CommissionRule object component (FR-3/FR-4)
        CnCommissionRuleObjectComponent,
        CnCommissionRuleObjectBaseViewComponent,
        CnCommissionRuleFullComponent,
        CnCommissionRuleSideComponent,

        // CommissionModel object component (FR-8)
        CnCommissionModelObjectComponent,
        CnCommissionModelObjectBaseViewComponent,
        CnCommissionModelFullComponent,
        CnCommissionModelSideComponent,

        // Comparison / snapshot (FR-10…FR-13)
        CnModelComparisonComponent,
        CnModelComparisonResultComponent,
        CnDecisionSnapshotComponent,

        // Trust / transparency (FR-14/FR-15/FR-17)
        CnExpectationTraceComponent,
        CnDataQualityPanelComponent,
        CnRulePreviewComponent,

        // Provisions-Dashboard inbox (P3)
        CnActivityInboxComponent,

        // Abgleich panel (P3)
        CnReconciliationPanelComponent,

        // Soll/Ist-Abgleich result tab on the Modellvergleich page
        CnAbgleichComparisonComponent,

        // Provisions-Import start (P3): Gesellschaft + Methode + KI-Agent-Prompt
        CnImportStartComponent,

        // Provisions-Eingangsfreigabe (P3): Gesellschaft + DRAFT-Liste + Bulk-Freigabe/Ablehnung
        CnImportReviewComponent,

        // Per-contract Vertragsabrechnung (embedded into the CM contract page)
        CnContractCommissionComponent,
    ],
    exports: [
        CnConfigPage,
        CnModelComparisonPage,
        CnDashboardPage,
        CnImportPage,
        CnImportReviewPage,
        CnReconciliationPage,
        CnCommissionModelObjectPage,
        CnCommissionRuleObjectPage,

        CnCommissionRuleObjectComponent,
        CnCommissionModelObjectComponent,

        CnModelComparisonComponent,
        CnModelComparisonResultComponent,
        CnDecisionSnapshotComponent,
        CnExpectationTraceComponent,
        CnDataQualityPanelComponent,
        CnRulePreviewComponent,

        // Per-contract Vertragsabrechnung — exported so the CM contract page can embed it.
        CnContractCommissionComponent,
    ],
    imports: [
        CoreModule,
        // p-splitter for the Soll Overview → Detail drill-down (Fiori FlexibleColumnLayout) on the dashboard.
        SplitterModule,
    ],
    providers: [CN_ENTITY_PROVIDER]
})
export class CnModule {
}
